import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../contexts/AuthContext';
import { StarRating } from './StarRating';
import { PluginComments } from './PluginComments';
import { useDownloadTracking } from '../hooks/useDownloadTracking';
import { usePluginBanners } from '../hooks/usePluginBanners';

interface PluginLink {
  id: string;
  type: string;
  display_name: string;
  url: string;
  icon: string;
  enabled: boolean;
  created_at: string;
}

interface Plugin {
  id: string;
  name: string;
  description: string;
  lastUpdated: string;
  color: string;
  wiki_url?: string;
  discord_url?: string;
  links?: PluginLink[];
  builds: {
    [key: string]: string[];
  };
}

interface PluginVersion {
  id: string;
  plugin_id: string;
  version: string;
  build_type: 'Release' | 'Beta' | 'Alpha' | 'DEV';
  changelog?: string;
  file_name: string;
  file_size: number;
  file_url: string;
  download_count: number;
  requires_dev_access: boolean;
  enabled: boolean;
  release_notes?: string;
  breaking_changes?: string[];
  bug_fixes?: string[];
  new_features?: string[];
  improvements?: string[];
  known_issues?: string[];
  created_at: string;
}

interface PluginStats {
  average_rating: number;
  total_ratings: number;
  total_comments: number;
  rating_distribution: { [key: string]: number };
  downloads: {
    total_downloads: number;
    downloads_today: number;
    downloads_this_week: number;
    downloads_this_month: number;
    unique_users: number;
  };
}

interface PluginDetailsModalProps {
  plugin: Plugin;
  isOpen: boolean;
  onClose: () => void;
  hasDevAccess: boolean;
  onStatsUpdate?: () => void;
}

export const PluginDetailsModal: React.FC<PluginDetailsModalProps> = ({
  plugin,
  isOpen,
  onClose,
  hasDevAccess,
  onStatsUpdate
}) => {
  const { user } = useAuth();
  const { logDownload, getEnhancedPluginStats } = useDownloadTracking();
  const { getPluginLinks } = usePluginBanners();
  const { getBannersForPlugin, getVersionBanner } = usePluginBanners();
  const [activeTab, setActiveTab] = useState<'download' | 'reviews' | 'comments'>('download');
  const [stats, setStats] = useState<PluginStats | null>(null);
  const [userRating, setUserRating] = useState<number>(0);
  const [selectedBuild, setSelectedBuild] = useState<string>('Release');
  const [selectedVersion, setSelectedVersion] = useState<PluginVersion | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [submittingRating, setSubmittingRating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dbVersions, setDbVersions] = useState<PluginVersion[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);

  useEffect(() => {
    if (isOpen && plugin) {
      loadPluginData();
      loadDatabaseVersions();
      setSelectedBuild('Release');
      setSelectedVersion(null);
      setActiveTab('download');
      setShowChangelog(false);
    }
  }, [isOpen, plugin]);

  const loadDatabaseVersions = async () => {
    try {
      setLoadingVersions(true);
      const { data, error } = await supabase
        .from('plugin_versions')
        .select('*')
        .eq('plugin_id', plugin.id)
        .eq('enabled', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading versions:', error);
      } else {
        setDbVersions(data || []);
      }
    } catch (err) {
      console.error('Error loading database versions:', err);
    } finally {
      setLoadingVersions(false);
    }
  };

  const getUserRating = async (pluginId: string): Promise<number> => {
    try {
      const { data: ratingData, error: ratingError } = await supabase
        .from('plugin_ratings')
        .select('rating')
        .eq('plugin_id', pluginId)
        .eq('user_id', user?.id)
        .single();

      if (ratingError && ratingError.code !== 'PGRST116') {
        console.error('Error loading user rating:', ratingError);
        return 0;
      }
      
      return ratingData?.rating || 0;
    } catch (error) {
      console.error('Error fetching user rating:', error);
      return 0;
    }
  };

  const loadPluginData = async () => {
    try {
      setLoadingStats(true);
      setError(null);

      // Load stats and user rating
      const [statsData, userRatingData] = await Promise.all([
        getEnhancedPluginStats(plugin.id),
        user ? getUserRating(plugin.id) : Promise.resolve(0)
      ]);

      setStats(statsData);
      setUserRating(userRatingData);
    } catch (error) {
      console.error('Error loading plugin data:', error);
      setError('Failed to load plugin data');
    } finally {
      setLoadingStats(false);
    }
  };

  const handleRatingChange = async (rating: number) => {
    if (!user || !plugin) {
      setError('You must be signed in to rate plugins');
      return;
    }

    try {
      setSubmittingRating(true);
      setError(null);

      const { data, error } = await supabase.rpc('upsert_plugin_rating', {
        plugin_id_param: plugin.id,
        rating_param: rating
      });

      if (error) {
        setError(error.message);
        return;
      }

      if (data?.success) {
        setUserRating(rating);
        // Reload local stats to show updated average
        await loadPluginData();
        // Trigger global stats refresh
        if (onStatsUpdate) {
          onStatsUpdate();
        }
      } else if (data?.error) {
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to submit rating');
      console.error('Error submitting rating:', err);
    } finally {
      setSubmittingRating(false);
    }
  };

  const handleDownload = async (version: PluginVersion) => {
    try {
      // First, try the signed URL approach
      const { data: signedUrlData, error: urlError } = await supabase.storage
        .from('plugin-files')
        .createSignedUrl(version.file_url, 300); // 5 minute expiry

      if (urlError) {
        console.error('Signed URL error:', urlError);
        
        // Fallback: Try direct file access if file_url is a direct URL
        if (version.file_url && version.file_url.startsWith('http')) {
          console.log('Using direct URL fallback for download');
          const link = document.createElement('a');
          link.href = version.file_url;
          link.download = version.file_name;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Log download
          if (plugin) {
            await logDownload(plugin.id, 'web');
          }
          
          // Trigger stats updates
          await loadPluginData();
          if (onStatsUpdate) {
            onStatsUpdate();
          }
          
          return;
        }
        
        // If no direct URL available, show error with instructions
        setError(`Download failed: Storage bucket not configured properly. Please contact the administrator to set up the 'plugin-files' storage bucket in Supabase.`);
        return;
      }

      // Log download
      if (plugin) {
        await logDownload(plugin.id, 'web');
      }

      // Start download using signed URL
      const link = document.createElement('a');
      link.href = signedUrlData.signedUrl;
      link.download = version.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Trigger stats updates (both local and global)
      await loadPluginData();
      if (onStatsUpdate) {
        onStatsUpdate();
      }
    } catch (error) {
      console.error('Download error:', error);
      setError('Failed to download file. Please try again.');
    }
  };

  const handleDownloadClick = async () => {
    if (!selectedVersion || !plugin) {
      setError('Please select a version to download!');
      return;
    }

    await handleDownload(selectedVersion);
  };

  // Group versions by build type and order them properly
  const getVersionsByBuild = () => {
    const grouped: { [key: string]: PluginVersion[] } = {};
    dbVersions.forEach(version => {
      if (!grouped[version.build_type]) {
        grouped[version.build_type] = [];
      }
      grouped[version.build_type].push(version);
    });
    return grouped;
  };

  // Define the proper order for build types
  const getBuildTypeOrder = () => {
    const versionsByBuild = getVersionsByBuild();
    const allBuildTypes = Object.keys(versionsByBuild);
    const orderedTypes = ['Release', 'Beta', 'Alpha', 'DEV'];
    
    // Filter to only include build types that actually exist
    return orderedTypes.filter(type => allBuildTypes.includes(type));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!isOpen) return null;

  const versionsByBuild = getVersionsByBuild();
  const availableBuildTypes = getBuildTypeOrder(); // Use proper ordering: Release, Beta, Alpha, DEV
  const versionsForSelectedBuild = versionsByBuild[selectedBuild] || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-theme-surface rounded-lg w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header - Mobile optimized */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-theme-border bg-theme-surface">
          <div className="flex-1">
            <h2 className="text-xl sm:text-2xl font-bold text-theme-text mb-1">{plugin.name}</h2>
            <p className="text-sm sm:text-base text-theme-text-secondary">{plugin.description}</p>
          </div>
          <button
            onClick={onClose}
            className="ml-4 p-2 text-theme-text-secondary hover:text-theme-text transition-colors rounded-md hover:bg-theme-surface-light"
          >
            <img 
              src="https://unpkg.com/pixelarticons@1.8.0/svg/close.svg" 
              alt="Close" 
              className="w-5 h-5 sm:w-6 sm:h-6"
              style={{ filter: `brightness(0) saturate(100%) invert(60%)` }}
            />
          </button>
        </div>

        {/* Tabs - Mobile optimized with horizontal scroll */}
        <div className="flex border-b border-theme-border bg-theme-surface-light overflow-x-auto scrollbar-hide">
          <button
            onClick={() => setActiveTab('download')}
            className={`px-4 sm:px-6 py-3 font-medium transition-colors whitespace-nowrap text-sm sm:text-base ${
              activeTab === 'download'
                ? 'text-theme-primary border-b-2 border-theme-primary'
                : 'text-theme-text-secondary hover:text-theme-text'
            }`}
          >
            Download
          </button>
          <button
            onClick={() => setActiveTab('reviews')}
            className={`px-4 sm:px-6 py-3 font-medium transition-colors whitespace-nowrap text-sm sm:text-base ${
              activeTab === 'reviews'
                ? 'text-theme-primary border-b-2 border-theme-primary'
                : 'text-theme-text-secondary hover:text-theme-text'
            }`}
          >
            Reviews
            {stats && stats.total_ratings > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-theme-surface text-xs rounded-full">
                {stats.total_ratings}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('comments')}
            className={`px-4 sm:px-6 py-3 font-medium transition-colors whitespace-nowrap text-sm sm:text-base ${
              activeTab === 'comments'
                ? 'text-theme-primary border-b-2 border-theme-primary'
                : 'text-theme-text-secondary hover:text-theme-text'
            }`}
          >
            Comments
            {stats && stats.total_comments > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-theme-surface text-xs rounded-full">
                {stats.total_comments}
              </span>
            )}
          </button>
        </div>

        {/* Content - Mobile optimized */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-900/30 border-2 border-dashed border-red-600 rounded-lg text-red-200 text-sm">
              {error}
            </div>
          )}

          {activeTab === 'download' && (
            <div className="space-y-4 sm:space-y-6">
              {loadingVersions ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-primary mx-auto"></div>
                  <p className="text-theme-text-secondary mt-2">Loading versions...</p>
                </div>
              ) : dbVersions.length === 0 ? (
                <div className="text-center py-12">
                  <img 
                    src="https://unpkg.com/pixelarticons@1.8.0/svg/inbox.svg" 
                    alt="No Versions" 
                    className="w-16 h-16 mx-auto mb-4 opacity-50"
                    style={{ filter: 'brightness(0) saturate(100%) invert(60%)' }}
                  />
                  <h3 className="text-xl font-semibold text-theme-text mb-2">No version available</h3>
                  <p className="text-theme-text-secondary">
                    The administrators haven't uploaded any versions for this plugin yet.
                  </p>
                </div>
              ) : (
                <>
                  {/* Version Selection - Mobile optimized */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label className="block text-sm font-medium text-theme-text-secondary mb-2">Build Type</label>
                      <div className="grid grid-cols-2 gap-2">
                        {getBuildTypeOrder().map((buildType) => {
                          const isDevLocked = buildType === 'DEV' && !hasDevAccess;
                          return (
                            <button
                              key={buildType}
                              onClick={() => {
                                if (!isDevLocked) {
                                  setSelectedBuild(buildType);
                                  setSelectedVersion(null);
                                }
                              }}
                              disabled={isDevLocked}
                              className={`p-2 sm:p-3 border-2 rounded-md transition-all duration-200 flex items-center justify-center gap-2 text-sm sm:text-base ${
                                selectedBuild === buildType
                                  ? 'border-current text-white'
                                  : isDevLocked 
                                    ? 'border-theme-border text-theme-text-muted cursor-not-allowed opacity-60'
                                    : 'border-theme-border text-theme-text-secondary hover:border-theme-border-light'
                              }`}
                              style={selectedBuild === buildType ? { borderColor: plugin.color, color: plugin.color } : {}}
                            >
                              {isDevLocked && (
                                <img 
                                  src="https://unpkg.com/pixelarticons@1.8.0/svg/lock.svg" 
                                  alt="Locked" 
                                  className="w-3 h-3 sm:w-4 sm:h-4"
                                  style={{ filter: 'brightness(0) saturate(100%) invert(60%)' }}
                                />
                              )}
                              {buildType}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-theme-text-secondary mb-2">Version</label>
                      <select
                        value={selectedVersion?.id || ''}
                        onChange={(e) => {
                          const version = versionsForSelectedBuild.find(v => v.id === e.target.value);
                          setSelectedVersion(version || null);
                        }}
                        className="w-full p-2 sm:p-3 bg-theme-surface border-2 border-theme-border rounded-md text-theme-text focus:border-theme-primary focus:outline-none"
                      >
                        <option value="">Select Version</option>
                        {versionsForSelectedBuild.map((version) => (
                          <option key={version.id} value={version.id}>
                            v{version.version} ({formatFileSize(version.file_size)})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Version Banner for unstable versions */}
                  {selectedBuild && ['Beta', 'Alpha', 'DEV'].includes(selectedBuild) && (
                    <div className="mb-4">
                      {(() => {
                        const versionBanner = getVersionBanner(selectedBuild);
                        if (!versionBanner) return null;
                        
                        return (
                          <div 
                            className="w-full p-4 rounded-lg border-2 border-dashed flex flex-col font-bold text-base"
                            style={{ 
                              backgroundColor: versionBanner.background_color,
                              borderColor: versionBanner.border_color,
                              color: versionBanner.text_color
                            }}
                          >
                            <div className="flex items-center gap-3 mb-2">
                              <img 
                                src={`https://unpkg.com/pixelarticons@1.8.0/svg/${versionBanner.icon_name}.svg`}
                                alt={versionBanner.text}
                                className="w-6 h-6 flex-shrink-0"
                                style={{ 
                                  filter: versionBanner.icon_filter
                                }}
                              />
                              <span className="font-bold">{versionBanner.text}</span>
                            </div>
                            {versionBanner.warning_subtext && (
                              <p className="text-sm opacity-80 font-normal ml-9">
                                {versionBanner.warning_subtext}
                              </p>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Selected Version Details */}
                  {selectedVersion && (
                    <div className="bg-theme-surface-light border-2 border-theme-border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-lg font-semibold text-theme-text">
                          v{selectedVersion.version}
                        </h4>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            selectedVersion.build_type === 'Release' ? 'bg-green-100 text-green-800' :
                            selectedVersion.build_type === 'Beta' ? 'bg-blue-100 text-blue-800' :
                            selectedVersion.build_type === 'Alpha' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {selectedVersion.build_type}
                          </span>
                          {selectedVersion.requires_dev_access && (
                            <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">
                              DEV ACCESS
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm mb-3">
                        <div>
                          <span className="text-theme-text-secondary">File:</span>
                          <p className="text-theme-text font-medium">{selectedVersion.file_name}</p>
                        </div>
                        <div>
                          <span className="text-theme-text-secondary">Size:</span>
                          <p className="text-theme-text font-medium">{formatFileSize(selectedVersion.file_size)}</p>
                        </div>
                        <div>
                          <span className="text-theme-text-secondary">Downloads:</span>
                          <p className="text-theme-text font-medium">{selectedVersion.download_count.toLocaleString()}</p>
                        </div>
                        <div>
                          <span className="text-theme-text-secondary">Released:</span>
                          <p className="text-theme-text font-medium">{new Date(selectedVersion.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>

                      {/* Changelog Button */}
                      {(selectedVersion.changelog || selectedVersion.release_notes || 
                        (selectedVersion.bug_fixes && selectedVersion.bug_fixes.length > 0) ||
                        (selectedVersion.new_features && selectedVersion.new_features.length > 0) ||
                        (selectedVersion.improvements && selectedVersion.improvements.length > 0) ||
                        (selectedVersion.breaking_changes && selectedVersion.breaking_changes.length > 0) ||
                        (selectedVersion.known_issues && selectedVersion.known_issues.length > 0)) && (
                        <button
                          onClick={() => setShowChangelog(!showChangelog)}
                          className="flex items-center gap-2 text-theme-primary hover:text-theme-primary-dark transition-colors text-sm font-medium"
                        >
                          <img 
                            src="https://unpkg.com/pixelarticons@1.8.0/svg/notes.svg" 
                            alt="Changelog" 
                            className="w-4 h-4"
                            style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(94%) saturate(7463%) hue-rotate(213deg) brightness(93%) contrast(94%)' }}
                          />
                          {showChangelog ? 'Hide' : 'View'} Changelog
                        </button>
                      )}

                      {/* Changelog Display */}
                      {showChangelog && (
                        <div className="mt-4 p-4 bg-theme-surface rounded-lg border border-theme-border">
                          <h5 className="text-md font-semibold text-theme-text mb-3">Changelog for v{selectedVersion.version}</h5>
                          
                          {selectedVersion.release_notes && (
                            <div className="mb-4">
                              <h6 className="text-sm font-medium text-theme-text mb-2">Release Notes</h6>
                              <p className="text-sm text-theme-text-secondary whitespace-pre-wrap">{selectedVersion.release_notes}</p>
                            </div>
                          )}

                          {selectedVersion.changelog && (
                            <div className="mb-4">
                              <h6 className="text-sm font-medium text-theme-text mb-2">Changelog</h6>
                              <p className="text-sm text-theme-text-secondary whitespace-pre-wrap">{selectedVersion.changelog}</p>
                            </div>
                          )}

                          {selectedVersion.new_features && selectedVersion.new_features.filter(f => f.trim()).length > 0 && (
                            <div className="mb-4">
                              <h6 className="text-sm font-medium text-green-600 mb-2">✨ New Features</h6>
                              <ul className="list-disc list-inside space-y-1">
                                {selectedVersion.new_features.filter(f => f.trim()).map((feature, index) => (
                                  <li key={index} className="text-sm text-theme-text-secondary">{feature}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {selectedVersion.improvements && selectedVersion.improvements.filter(i => i.trim()).length > 0 && (
                            <div className="mb-4">
                              <h6 className="text-sm font-medium text-blue-600 mb-2">⚡ Improvements</h6>
                              <ul className="list-disc list-inside space-y-1">
                                {selectedVersion.improvements.filter(i => i.trim()).map((improvement, index) => (
                                  <li key={index} className="text-sm text-theme-text-secondary">{improvement}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {selectedVersion.bug_fixes && selectedVersion.bug_fixes.filter(f => f.trim()).length > 0 && (
                            <div className="mb-4">
                              <h6 className="text-sm font-medium text-orange-600 mb-2">🐛 Bug Fixes</h6>
                              <ul className="list-disc list-inside space-y-1">
                                {selectedVersion.bug_fixes.filter(f => f.trim()).map((fix, index) => (
                                  <li key={index} className="text-sm text-theme-text-secondary">{fix}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {selectedVersion.breaking_changes && selectedVersion.breaking_changes.filter(c => c.trim()).length > 0 && (
                            <div className="mb-4">
                              <h6 className="text-sm font-medium text-red-600 mb-2">⚠️ Breaking Changes</h6>
                              <ul className="list-disc list-inside space-y-1">
                                {selectedVersion.breaking_changes.filter(c => c.trim()).map((change, index) => (
                                  <li key={index} className="text-sm text-theme-text-secondary">{change}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {selectedVersion.known_issues && selectedVersion.known_issues.filter(i => i.trim()).length > 0 && (
                            <div className="mb-4">
                              <h6 className="text-sm font-medium text-purple-600 mb-2">🚨 Known Issues</h6>
                              <ul className="list-disc list-inside space-y-1">
                                {selectedVersion.known_issues.filter(i => i.trim()).map((issue, index) => (
                                  <li key={index} className="text-sm text-theme-text-secondary">{issue}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Download Button - Mobile optimized */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={onClose}
                      className="order-2 sm:order-1 flex-1 p-3 bg-theme-surface border-2 border-theme-border text-theme-text hover:bg-theme-surface-light transition-colors rounded-md"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDownloadClick}
                      disabled={!selectedVersion}
                      className="order-1 sm:order-2 flex-1 p-3 font-medium transition-all duration-200 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      style={{ backgroundColor: selectedVersion ? plugin.color : '#6b7280' }}
                    >
                      <img 
                        src="https://unpkg.com/pixelarticons@1.8.0/svg/download.svg" 
                        alt="Download" 
                        className="w-4 h-4"
                        style={{ filter: `brightness(0) saturate(100%) invert(1)` }}
                      />
                      Download {selectedVersion ? `v${selectedVersion.version}` : 'Plugin'}
                    </button>
                  </div>
                </>
              )}

              {/* Plugin Statistics - Mobile optimized */}
              {stats && !loadingStats && (
                <div className="bg-theme-surface-light border-2 border-theme-border rounded-lg p-4 sm:p-6">
                  <h3 className="text-lg font-semibold text-theme-text mb-4">Plugin Statistics</h3>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    <div className="text-center p-3 bg-theme-surface rounded-lg">
                      <div className="text-lg sm:text-2xl font-bold text-theme-primary">{stats.downloads?.total_downloads?.toLocaleString() || '0'}</div>
                      <div className="text-xs sm:text-sm text-theme-text-muted">Total Downloads</div>
                    </div>
                    <div className="text-center p-3 bg-theme-surface rounded-lg">
                      <div className="text-lg sm:text-2xl font-bold text-green-500">{stats.average_rating?.toFixed(1) || '0.0'}</div>
                      <div className="text-xs sm:text-sm text-theme-text-muted">Average Rating</div>
                    </div>
                    <div className="text-center p-3 bg-theme-surface rounded-lg">
                      <div className="text-lg sm:text-2xl font-bold text-blue-500">{stats.total_ratings || 0}</div>
                      <div className="text-xs sm:text-sm text-theme-text-muted">Total Ratings</div>
                    </div>
                    <div className="text-center p-3 bg-theme-surface rounded-lg">
                      <div className="text-lg sm:text-2xl font-bold text-purple-500">{stats.total_comments || 0}</div>
                      <div className="text-xs sm:text-sm text-theme-text-muted">Comments</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Plugin Links - Comprehensive display */}
              {plugin.links && plugin.links.length > 0 && (
                <div className="mt-4 p-4 bg-theme-surface-light rounded-lg border border-theme-border">
                  <h4 className="text-sm font-medium text-theme-text mb-3">Plugin Links:</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {plugin.links
                      .filter(link => link.enabled)
                      .map((link) => (
                        <a
                          key={link.id}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-3 p-3 bg-theme-surface rounded-lg border border-theme-border hover:border-theme-primary transition-all group"
                        >
                          <div className="flex-shrink-0">
                            <img 
                              src={`https://unpkg.com/pixelarticons@1.8.0/svg/${link.icon}.svg`}
                              alt={link.display_name}
                              className="w-5 h-5 transition-transform group-hover:scale-110"
                              style={{ filter: 'brightness(0) saturate(100%) invert(60%)' }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-theme-text group-hover:text-theme-primary transition-colors">
                              {link.display_name}
                            </div>
                            <div className="text-xs text-theme-text-muted capitalize">
                              {link.type}
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            <img 
                              src="https://unpkg.com/pixelarticons@1.8.0/svg/external-link.svg"
                              alt="External Link"
                              className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity"
                              style={{ filter: 'brightness(0) saturate(100%) invert(60%)' }}
                            />
                          </div>
                        </a>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'reviews' && user && (
            <div className="space-y-4 sm:space-y-6">
              {/* Rating Form - Mobile optimized */}
              <div className="bg-theme-surface-light border-2 border-theme-border rounded-lg p-4 sm:p-6">
                <h3 className="text-lg font-semibold text-theme-text mb-4">Rate This Plugin</h3>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="flex items-center gap-2">
                    <StarRating 
                      rating={userRating} 
                      readonly={false} 
                      size="lg" 
                      onRatingChange={setUserRating}
                    />
                    <span className="text-sm text-theme-text-secondary ml-2">
                      ({userRating}/5)
                    </span>
                  </div>
                  <button
                    onClick={() => handleRatingChange(userRating)}
                    disabled={userRating === 0 || submittingRating}
                    className="w-full sm:w-auto px-4 py-2 bg-theme-primary text-white rounded-md hover:bg-opacity-80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {submittingRating ? 'Submitting...' : 'Submit Rating'}
                  </button>
                </div>
              </div>

              {/* Rating Statistics - Mobile optimized */}
              {stats && stats.total_ratings > 0 && (
                <div className="bg-theme-surface-light border-2 border-theme-border rounded-lg p-4 sm:p-6">
                  <h3 className="text-lg font-semibold text-theme-text mb-4">Rating Overview</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div className="text-center">
                      <div className="text-3xl sm:text-4xl font-bold text-theme-primary mb-2">
                        {stats.average_rating.toFixed(1)}
                      </div>
                      <StarRating rating={stats.average_rating} readonly size="lg" />
                      <div className="text-sm text-theme-text-muted mt-2">
                        Based on {stats.total_ratings} review{stats.total_ratings !== 1 ? 's' : ''}
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      {[5, 4, 3, 2, 1].map((star) => {
                        const count = stats.rating_distribution?.[star] || 0;
                        const percentage = stats.total_ratings > 0 ? (count / stats.total_ratings) * 100 : 0;
                        return (
                          <div key={star} className="flex items-center gap-2 text-sm">
                            <span className="w-8 text-theme-text">{star}★</span>
                            <div className="flex-1 bg-theme-surface rounded-full h-2">
                              <div 
                                className="bg-theme-primary h-2 rounded-full transition-all duration-300"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className="w-8 text-right text-theme-text-muted">{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'reviews' && !user && (
            <div className="text-center py-8 sm:py-12">
              <img 
                src="https://unpkg.com/pixelarticons@1.8.0/svg/user.svg" 
                alt="Login Required" 
                className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 opacity-50"
                style={{ filter: 'brightness(0) saturate(100%) invert(60%)' }}
              />
              <h3 className="text-lg sm:text-xl font-semibold text-theme-text mb-2">Login Required</h3>
              <p className="text-sm sm:text-base text-theme-text-secondary">
                Please log in to rate and review this plugin.
              </p>
            </div>
          )}

          {activeTab === 'comments' && (
            <PluginComments 
              pluginId={plugin.id} 
              onStatsUpdate={async () => {
                await loadPluginData();
                if (onStatsUpdate) {
                  onStatsUpdate();
                }
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}; 